/*
 * GH2: cart protection add-on.
 * Keeps one tier of the protection product in the cart (picked from the cart value),
 * hides it from the item list and shows it as a row above the checkout button with a
 * "Continue without" option underneath. Configured in Header > GH2 style overrides.
 */
(() => {
  if (window.GH2CartProtection) return;

  const configElement = document.getElementById('CartProtectionConfig');
  if (!configElement) return;

  let config;
  try {
    config = JSON.parse(configElement.textContent);
  } catch (e) {
    return;
  }

  const OPT_OUT_KEY = '_protection_opt_out';
  const routes = window.routes || {};
  const cartUrl = routes.cart_url || '/cart';
  const cartUpdateUrl = routes.cart_update_url || '/cart/update';
  const checkoutUrl = `${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}checkout`;

  const parseRange = (title) => {
    const clean = String(title).replace(/[$,\s]/g, '');
    if (/\+$/.test(clean)) return { min: parseFloat(clean), max: Infinity };
    const match = clean.match(/^(\d+(?:\.\d+)?)[-–](\d+(?:\.\d+)?)$/);
    if (match) return { min: parseFloat(match[1]), max: parseFloat(match[2]) };
    const value = parseFloat(clean);
    return { min: value, max: value };
  };

  const tiers = (config.variants || [])
    .map((variant) => ({ ...variant, id: Number(variant.id), ...parseRange(variant.title) }))
    .filter((tier) => !Number.isNaN(tier.max))
    .sort((a, b) => a.max - b.max);
  if (!tiers.length) return;

  const variantIds = new Set(tiers.map((tier) => tier.id));

  const formatMoney = (cents, dropWholeDecimals = false) => {
    const format = String(config.moneyFormat || '${{amount}}').replace(/<[^>]*>/g, '');
    const value = (Number(cents) || 0) / 100;
    const whole = dropWholeDecimals && Number(cents) % 100 === 0;
    const build = (decimals, thousands = ',', decimal = '.') => {
      const parts = value.toFixed(decimals).split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return parts.join(decimal);
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return build(0);
        case 'amount_with_comma_separator':
          return whole ? build(0, '.', ',') : build(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return build(0, '.', ',');
        case 'amount_with_apostrophe_separator':
          return whole ? build(0, "'") : build(2, "'");
        default:
          return whole ? build(0) : build(2);
      }
    });
  };

  const escapeHtml = (text) =>
    String(text).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);

  const state = { cart: null, syncing: null, dirty: false, busy: false };

  const isProtection = (item) => variantIds.has(Number(item.variant_id));
  const otherItems = (cart) => cart.items.filter((item) => !isProtection(item));
  const optedOut = (cart) => Boolean(cart && cart.attributes && cart.attributes[OPT_OUT_KEY] === 'true');

  const tierFor = (cart) => {
    const value = otherItems(cart).reduce((sum, item) => sum + item.final_line_price, 0) / 100;
    return tiers.find((tier) => value <= tier.max) || tiers[tiers.length - 1];
  };

  const getCart = () =>
    fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } }).then((response) => response.json());

  const sectionTargets = () => {
    const targets = [];
    document.querySelectorAll('cart-drawer-items, cart-items').forEach((element) => {
      if (typeof element.getSectionsToRender !== 'function') return;
      try {
        element.getSectionsToRender().forEach((section) => {
          if (section.section && !targets.some((target) => target.id === section.id)) targets.push(section);
        });
      } catch (e) {
        /* section not on this page */
      }
    });
    return targets;
  };

  const updateCart = async (body) => {
    const targets = sectionTargets();
    const payload = { ...body };
    if (targets.length) {
      payload.sections = [...new Set(targets.map((target) => target.section))];
      payload.sections_url = window.location.pathname;
    }

    const response = await fetch(`${cartUpdateUrl}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    });
    const cart = await response.json();
    if (cart.status) throw new Error(cart.description || cart.message || 'Cart update failed');

    const empty = cart.item_count === 0;
    document.querySelector('cart-drawer')?.classList.toggle('is-empty', empty);
    document.getElementById('main-cart-footer')?.classList.toggle('is-empty', empty);
    document.querySelector('cart-items')?.classList.toggle('is-empty', empty);

    if (cart.sections) {
      targets.forEach((target) => {
        const html = cart.sections[target.section];
        const container = document.getElementById(target.id);
        if (!html || !container) return;
        const element = (target.selector && container.querySelector(target.selector)) || container;
        const source = new DOMParser().parseFromString(html, 'text/html').querySelector(target.selector || '.shopify-section');
        if (source) element.innerHTML = source.innerHTML;
      });
    }
    return cart;
  };

  const planUpdates = (cart) => {
    const protectionLines = cart.items.filter(isProtection);
    const wantedId = otherItems(cart).length && !optedOut(cart) ? tierFor(cart).id : null;
    const updates = {};
    let kept = false;

    protectionLines.forEach((line) => {
      if (wantedId && Number(line.variant_id) === wantedId && !kept) {
        kept = true;
        if (line.quantity !== 1) updates[line.key] = 1;
      } else {
        updates[line.key] = 0;
      }
    });
    if (wantedId && !kept) updates[wantedId] = 1;

    return Object.keys(updates).length ? updates : null;
  };

  const fixCountBubble = () => {
    const cart = state.cart;
    if (!cart) return;
    const protectionQuantity = cart.items.filter(isProtection).reduce((sum, item) => sum + item.quantity, 0);
    if (!protectionQuantity) return;
    const count = Math.max(cart.item_count - protectionQuantity, 0);
    document.querySelectorAll('#cart-icon-bubble .cart-count-bubble').forEach((bubble) => {
      if (count === 0) {
        bubble.remove();
        return;
      }
      const visible = bubble.querySelector('span[aria-hidden="true"]');
      if (visible && visible.textContent !== String(count)) visible.textContent = String(count);
      const hidden = bubble.querySelector('.visually-hidden');
      if (hidden) {
        const text = hidden.textContent.replace(/\d+/, String(count));
        if (hidden.textContent !== text) hidden.textContent = text;
      }
    });
  };

  const decorate = () => {
    const cart = state.cart;
    if (!cart) return;

    variantIds.forEach((id) => {
      document.querySelectorAll(`[data-quantity-variant-id="${id}"]`).forEach((input) => {
        input.closest('.cart-item')?.classList.add('cart-item--protection');
      });
    });
    fixCountBubble();

    const hasItems = otherItems(cart).length > 0;
    const active = cart.items.find(isProtection);
    const isOptedOut = optedOut(cart);
    const tier = tierFor(cart);
    const priceCents = active ? active.final_line_price : tier.price;
    const signature = [hasItems, active ? active.variant_id : 0, tier.id, priceCents, isOptedOut].join('|');

    document.querySelectorAll('.cart__ctas').forEach((ctas) => {
      const button = ctas.querySelector('.cart__checkout-button');
      if (!button) return;

      let row = ctas.previousElementSibling && ctas.previousElementSibling.matches('.cart-protection') ? ctas.previousElementSibling : null;
      let optOut = ctas.nextElementSibling && ctas.nextElementSibling.matches('.cart-protection__optout') ? ctas.nextElementSibling : null;

      if (!hasItems) {
        row?.remove();
        optOut?.remove();
      } else if (!row || row.dataset.signature !== signature) {
        const action = isOptedOut
          ? `<button type="button" class="cart-protection__add" data-cart-protection-add>${escapeHtml(config.addLabel || 'Add')} +${escapeHtml(formatMoney(priceCents))}</button>`
          : `<span class="cart-protection__price">${escapeHtml(formatMoney(priceCents))}</span>`;
        const html = `<div class="cart-protection__text"><p class="cart-protection__title">${escapeHtml(config.title || '')}</p>${
          config.subtitle ? `<p class="cart-protection__subtitle">${escapeHtml(config.subtitle)}</p>` : ''
        }</div><div class="cart-protection__action">${action}</div>`;

        if (!row) {
          row = document.createElement('div');
          row.className = 'cart-protection';
          ctas.insertAdjacentElement('beforebegin', row);
        }
        row.innerHTML = html;
        row.dataset.signature = signature;
      }

      if (hasItems && !isOptedOut) {
        if (!optOut) {
          optOut = document.createElement('a');
          optOut.className = 'cart-protection__optout';
          optOut.href = checkoutUrl;
          optOut.setAttribute('data-cart-protection-optout', '');
          optOut.textContent = config.optOutLabel || 'Continue without protection';
          ctas.insertAdjacentElement('afterend', optOut);
        }
      } else if (optOut) {
        optOut.remove();
      }

      if (config.showTotal) {
        if (!button.dataset.baseLabel) button.dataset.baseLabel = button.textContent.trim();
        const label = hasItems ? `${button.dataset.baseLabel} · ${formatMoney(cart.total_price, true)}` : button.dataset.baseLabel;
        if (button.textContent !== label) button.textContent = label;
      }
    });
  };

  const sync = () => {
    if (state.syncing) {
      state.dirty = true;
      return state.syncing;
    }
    state.syncing = (async () => {
      try {
        do {
          state.dirty = false;
          let cart = await getCart();
          state.cart = cart;
          const updates = planUpdates(cart);
          if (updates) {
            cart = await updateCart({ updates });
            state.cart = cart;
          }
          decorate();
        } while (state.dirty);
      } catch (e) {
        console.error(e);
      } finally {
        state.syncing = null;
      }
    })();
    return state.syncing;
  };

  const setProtection = async (enabled) => {
    if (state.syncing) await state.syncing;
    const cart = state.cart || (await getCart());
    const updates = {};
    cart.items.filter(isProtection).forEach((line) => {
      updates[line.key] = 0;
    });
    if (enabled && otherItems(cart).length) updates[tierFor(cart).id] = 1;
    state.cart = await updateCart({ updates, attributes: { [OPT_OUT_KEY]: enabled ? '' : 'true' } });
    decorate();
  };

  document.addEventListener('click', async (event) => {
    const add = event.target.closest('[data-cart-protection-add]');
    const optOut = event.target.closest('[data-cart-protection-optout]');
    if (!add && !optOut) return;
    event.preventDefault();
    if (state.busy) return;
    state.busy = true;
    (add || optOut).setAttribute('aria-disabled', 'true');
    try {
      if (add) {
        await setProtection(true);
      } else {
        await setProtection(false);
        window.location.href = checkoutUrl;
      }
    } catch (e) {
      console.error(e);
      if (optOut) window.location.href = checkoutUrl;
    } finally {
      state.busy = false;
    }
  });

  // Checkout buttons go straight to checkout once the protection line is up to date.
  document.addEventListener(
    'click',
    async (event) => {
      const button = event.target.closest('button[name="checkout"]');
      if (!button || button.disabled || !button.closest('cart-drawer, #main-cart-footer')) return;
      event.preventDefault();
      event.stopPropagation();
      if (button.getAttribute('aria-disabled') === 'true') return;
      button.setAttribute('aria-disabled', 'true');
      try {
        if (state.syncing) await state.syncing;
      } finally {
        window.location.href = checkoutUrl;
      }
    },
    true
  );

  const observeContent = () => {
    const observer = new MutationObserver(() => decorate());
    ['cart-drawer', '#main-cart-items', '#main-cart-footer', '#cart-icon-bubble'].forEach((selector) => {
      const element = document.querySelector(selector);
      if (element) observer.observe(element, { childList: true, subtree: true });
    });

    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      let wasOpen = drawer.classList.contains('active');
      new MutationObserver(() => {
        const isOpen = drawer.classList.contains('active');
        if (isOpen && !wasOpen) sync();
        wasOpen = isOpen;
      }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    }
  };

  const init = () => {
    observeContent();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => sync());
    }
    if (document.getElementById('main-cart-items')) {
      sync();
    } else {
      getCart()
        .then((cart) => {
          state.cart = cart;
          decorate();
        })
        .catch(() => {});
    }
  };

  // Re-enable buttons if the shopper comes back with the browser's Back button.
  window.addEventListener('pageshow', () => {
    document
      .querySelectorAll('button[name="checkout"][aria-disabled="true"], [data-cart-protection-optout][aria-disabled="true"]')
      .forEach((element) => element.removeAttribute('aria-disabled'));
  });

  window.GH2CartProtection = { sync };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
